import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'react-bootstrap';
import { Input, Modal as AntModal, Select, Spin, Tag } from 'antd';
import { useAppDispatch, useAppSelector } from 'core/store/hooks';
import { getClFilters } from 'pages/private/management-tools/_redux/actions';
import { filtriClSelector } from 'pages/private/management-tools/_redux/selectors';
import AppCard from 'shared/design-system/components/app-card';
import LinksButton from 'shared/design-system/components/links-button';
import NoData from 'shared/design-system/components/no-data-box';
import { AppToastService } from 'shared/design-system/components/app-toast/app-toast.service';
import { filterOption } from 'shared/utils/common.utils';
import {
  AccountOptionDto,
  DimAlberoCommercialeL1Dto,
  DimAlberoCommercialeL2Dto,
  DimAlberoCommercialeL3Dto,
} from '@api/client';
import { crudGetAlberaturaBusinessNew } from '../_redux/actions';
import {
  deleteAccountStrutturaBusinessAssociation,
  getAccountsByStruttura,
  getAllAccountStrutturaBusiness,
  saveAccountStrutturaBusinessAssociation,
} from './_redux/actions';

type Level = 'L1' | 'L2' | 'L3';

interface StrutturaNode {
  id: number;
  name: string;
  level: Level;
  key: string; // unique tra livelli (es. 'L2-123')
  children: StrutturaNode[];
}

const buildTree = (tree: DimAlberoCommercialeL1Dto[]): StrutturaNode[] => {
  const out: StrutturaNode[] = [];
  tree.forEach((l1) => {
    if (l1.idStrutturaFunzione == null) return;
    const l1Node: StrutturaNode = {
      id: l1.idStrutturaFunzione,
      name: l1.nomeStrutturaFunzione || `L1 ${l1.idStrutturaFunzione}`,
      level: 'L1',
      key: `L1-${l1.idStrutturaFunzione}`,
      children: [],
    };
    (l1.l2Children || []).forEach((l2: DimAlberoCommercialeL2Dto) => {
      if (l2.l2 == null) return;
      const l2Node: StrutturaNode = {
        id: l2.l2,
        name: l2.l2NomeStrutturaFunzione || `L2 ${l2.l2}`,
        level: 'L2',
        key: `L2-${l2.l2}`,
        children: [],
      };
      (l2.l3Children || []).forEach((l3: DimAlberoCommercialeL3Dto) => {
        if (l3.l3 == null) return;
        l2Node.children.push({
          id: l3.l3,
          name: l3.l3NomeStrutturaFunzione || `L3 ${l3.l3}`,
          level: 'L3',
          key: `L3-${l3.l3}`,
          children: [],
        });
      });
      l1Node.children.push(l2Node);
    });
    out.push(l1Node);
  });
  return out;
};

const LEVEL_COLOR: Record<Level, string> = {
  L1: '#304BA2',
  L2: '#5a73c5',
  L3: '#8f9bd0',
};

// Filtra ricorsivamente l'albero mantenendo solo i nodi attivi (id presente nel set).
// Conserva l'antenato se un discendente è attivo.
const filterTreeByActive = (nodes: StrutturaNode[], activeIds: Set<number>): StrutturaNode[] => {
  const out: StrutturaNode[] = [];
  nodes.forEach((n) => {
    const children = filterTreeByActive(n.children, activeIds);
    if (activeIds.has(n.id) || children.length > 0) {
      out.push({ ...n, children });
    }
  });
  return out;
};

// Filtra ricorsivamente per nome; conserva l'antenato se un discendente matcha
const filterTreeByName = (nodes: StrutturaNode[], q: string): StrutturaNode[] => {
  if (!q) return nodes;
  const out: StrutturaNode[] = [];
  nodes.forEach((n) => {
    const children = filterTreeByName(n.children, q);
    if (n.name.toLowerCase().includes(q) || children.length > 0) {
      out.push({ ...n, children });
    }
  });
  return out;
};

// Restituisce le key dei nodi con figli (utile per auto-expand quando si filtra)
const collectExpandableKeys = (nodes: StrutturaNode[], acc: Record<string, boolean> = {}): Record<string, boolean> => {
  nodes.forEach((n) => {
    if (n.children.length > 0) {
      acc[n.key] = true;
      collectExpandableKeys(n.children, acc);
    }
  });
  return acc;
};

interface RenderOpts {
  selectedKey?: string;
  expanded: Record<string, boolean>;
  onToggle: (key: string) => void;
  onSelect: (node: StrutturaNode) => void;
}

const renderNodes = (nodes: StrutturaNode[], depth: number, opts: RenderOpts): React.ReactNode => {
  return nodes.map((n) => {
    const hasChildren = n.children.length > 0;
    const isOpen = !!opts.expanded[n.key];
    const isSelected = opts.selectedKey === n.key;
    return (
      <div key={n.key}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            padding: '6px 8px',
            borderRadius: 6,
            backgroundColor: isSelected ? '#eef2fb' : 'transparent',
            borderLeft: `3px solid ${isSelected ? LEVEL_COLOR[n.level] : 'transparent'}`,
            marginBottom: 2,
            paddingLeft: 10 + depth * 16,
          }}
          onClick={() => opts.onSelect(n)}
        >
          <span
            onClick={(e) => {
              e.stopPropagation();
              if (hasChildren) opts.onToggle(n.key);
            }}
            style={{
              width: 18,
              display: 'inline-flex',
              justifyContent: 'center',
              color: hasChildren ? '#666' : 'transparent',
              cursor: hasChildren ? 'pointer' : 'default',
            }}
          >
            <i className={`fas fa-chevron-${isOpen ? 'down' : 'right'}`} style={{ fontSize: '0.7rem' }} />
          </span>
          <Tag color={LEVEL_COLOR[n.level]} style={{ marginRight: 8 }}>
            {n.level}
          </Tag>
          <span style={{ fontWeight: isSelected ? 600 : 400 }}>{n.name}</span>
        </div>
        {hasChildren && isOpen && renderNodes(n.children, depth + 1, opts)}
      </div>
    );
  });
};

const AccountBusinessStructureCrud: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const filtriCl = useAppSelector(filtriClSelector);
  const [accounts, setAccounts] = useState<AccountOptionDto[]>([]);

  const [strutture, setStrutture] = useState<StrutturaNode[]>([]);
  const [loadingStrutture, setLoadingStrutture] = useState(false);
  const [strutturaSearch, setStrutturaSearch] = useState('');
  const [selectedStruttura, setSelectedStruttura] = useState<StrutturaNode | undefined>(undefined);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const [associations, setAssociations] = useState<AccountOptionDto[]>([]);
  const [loadingAssociations, setLoadingAssociations] = useState(false);

  const [showAddModal, setShowAddModal] = useState(false);
  const [accountToAdd, setAccountToAdd] = useState<number | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    dispatch(getAllAccountStrutturaBusiness())
      .unwrap()
      .then((res) => setAccounts(res || []));
    if (!filtriCl?.businessUnit?.length) dispatch(getClFilters());
    setLoadingStrutture(true);
    dispatch(crudGetAlberaturaBusinessNew({}))
      .unwrap()
      .then((res) => setStrutture(buildTree(res || [])))
      .finally(() => setLoadingStrutture(false));
  }, []);

  // Set degli id delle strutture attive (da /cl/search-filters-values)
  const activeIds = useMemo<Set<number>>(() => {
    const ids = (filtriCl?.businessUnit || [])
      .map((bu) => bu.id)
      .filter((id): id is number => id != null);
    return new Set(ids);
  }, [filtriCl?.businessUnit]);

  const activeTree = useMemo(() => filterTreeByActive(strutture, activeIds), [strutture, activeIds]);

  const filteredTree = useMemo(() => {
    const q = strutturaSearch.trim().toLowerCase();
    return filterTreeByName(activeTree, q);
  }, [activeTree, strutturaSearch]);

  // Durante la ricerca apriamo automaticamente i nodi che contengono match
  const effectiveExpanded = useMemo<Record<string, boolean>>(() => {
    if (strutturaSearch.trim()) return collectExpandableKeys(filteredTree);
    return expanded;
  }, [filteredTree, strutturaSearch, expanded]);

  const toggleExpand = (key: string) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const loadAssociations = (idStruttura: number) => {
    setLoadingAssociations(true);
    dispatch(getAccountsByStruttura(idStruttura))
      .unwrap()
      .then((res) => setAssociations(res || []))
      .finally(() => setLoadingAssociations(false));
  };

  const handleSelectStruttura = (s: StrutturaNode) => {
    setSelectedStruttura(s);
    loadAssociations(s.id);
  };

  // L'AccountOptionDto include ora anche un campo numerico `id` (FK DB).
  // È quello che va inviato al BE come `idAccount` per associare/dissociare.
  const getAccountId = (a: AccountOptionDto): number | undefined => a?.id;

  const accountsAlreadyAssociated = useMemo(
    () => new Set(associations.map(getAccountId).filter((v): v is number => v != null)),
    [associations],
  );

  const accountsAvailableForAdd = useMemo(
    () => accounts.filter((a) => {
      const id = getAccountId(a);
      return id != null && !accountsAlreadyAssociated.has(id);
    }),
    [accounts, accountsAlreadyAssociated],
  );

  const handleAddAssociation = () => {
    if (!selectedStruttura || accountToAdd == null) return;
    setSaving(true);
    dispatch(
      saveAccountStrutturaBusinessAssociation({
        createUpdateAccountStrutturaBusinessDto: {
          idAccount: accountToAdd,
          idStrutturaBusiness: selectedStruttura.id,
        },
      }),
    )
      .unwrap()
      .then((res) => {
        if (res) {
          AppToastService.success(t('Associazione creata con successo'));
          setShowAddModal(false);
          setAccountToAdd(undefined);
          loadAssociations(selectedStruttura.id);
        }
      })
      .finally(() => setSaving(false));
  };

  const handleDelete = (assoc: AccountOptionDto) => {
    if (!selectedStruttura) return;
    // L'id dell'account (numerico, restituito dal nuovo campo `id` del listing) va inviato come idAccount.
    const accId = Number((assoc as any)?.id);
    // eslint-disable-next-line no-console
    console.log('[account-struttura-business] delete clicked', { assoc, accId, struttura: selectedStruttura.id });
    if (!Number.isFinite(accId)) {
      AppToastService.error(t('Impossibile eliminare: id account non disponibile'));
      return;
    }
    AntModal.confirm({
      title: t('Conferma eliminazione'),
      content: t('Sei sicuro di voler rimuovere questa associazione?'),
      okText: t('Elimina'),
      okType: 'danger',
      cancelText: t('common.cancel'),
      onOk: () => {
        // eslint-disable-next-line no-console
        console.log('[account-struttura-business] dispatching delete', {
          idAccount: accId,
          idStrutturaBusiness: selectedStruttura.id,
        });
        return dispatch(
          deleteAccountStrutturaBusinessAssociation({
            idAccount: accId,
            idStrutturaBusiness: selectedStruttura.id,
          }),
        )
          .unwrap()
          .then(() => {
            AppToastService.success(t('Associazione rimossa con successo'));
            loadAssociations(selectedStruttura.id);
          })
          .catch((err) => {
            // eslint-disable-next-line no-console
            console.error('[account-struttura-business] delete failed', err);
          });
      },
    });
  };

  return (
    <div className="mt-3">
      <Row>
        <Col lg={5} md={6} sm={12} className="mb-3">
          <AppCard title={t('Strutture business')}>
            <Input.Search
              placeholder={t('Cerca struttura')}
              value={strutturaSearch}
              onChange={(e) => setStrutturaSearch(e.target.value)}
              allowClear
              className="mb-2"
            />
            {loadingStrutture ? (
              <div className="d-flex justify-content-center py-4">
                <Spin />
              </div>
            ) : filteredTree.length === 0 ? (
              <NoData msg={t('Nessuna struttura trovata')} />
            ) : (
              <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                {renderNodes(filteredTree, 0, {
                  selectedKey: selectedStruttura?.key,
                  expanded: effectiveExpanded,
                  onToggle: toggleExpand,
                  onSelect: handleSelectStruttura,
                })}
              </div>
            )}
          </AppCard>
        </Col>

        <Col lg={7} md={6} sm={12} className="mb-3">
          <AppCard
            title={
              selectedStruttura
                ? `${t('Account associati')} — ${selectedStruttura.name}`
                : t('Account associati')
            }
            headerElement={
              selectedStruttura ? (
                <LinksButton
                  className="btn-primary btn-links btn-sm ms-4"
                  onClick={() => setShowAddModal(true)}
                >
                  <i className="fas fa-plus me-2" />
                  {t('Associa account')}
                </LinksButton>
              ) : undefined
            }
          >
            {!selectedStruttura ? (
              <NoData msg={t('Seleziona una struttura per gestire gli account associati')} />
            ) : loadingAssociations ? (
              <div className="d-flex justify-content-center py-4">
                <Spin />
              </div>
            ) : associations.length === 0 ? (
              <NoData msg={t('Nessun account associato a questa struttura')} />
            ) : (
              <div className="table-responsive table-bordered">
                <table className="table mt-0 mb-0">
                  <thead className="bg-red">
                    <tr>
                      <th>{t('Account')}</th>
                      <th className="text-end" style={{ width: 80 }}>
                        {t('Azioni')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {associations.map((a, i) => (
                      <tr key={a.idAccount ?? `tmp-${i}`}>
                        <td>{a.nome || a.idAccount || '-'}</td>
                        <td className="text-end">
                          <i
                            role="button"
                            className="fas fa-trash text-danger"
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleDelete(a)}
                            title={t('Elimina')}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </AppCard>
        </Col>
      </Row>

      <AntModal
        title={t('Associa account')}
        open={showAddModal}
        onCancel={() => {
          setShowAddModal(false);
          setAccountToAdd(undefined);
        }}
        footer={[
          <LinksButton
            key="cancel"
            className="btn btn-links-outline me-2"
            onClick={() => {
              setShowAddModal(false);
              setAccountToAdd(undefined);
            }}
          >
            {t('common.cancel')}
          </LinksButton>,
          <LinksButton
            key="save"
            className="btn-primary btn-links"
            disabled={accountToAdd == null || saving}
            onClick={handleAddAssociation}
          >
            {saving ? <Spin size="small" className="me-2" /> : <i className="fas fa-check me-2" />}
            {t('common.save')}
          </LinksButton>,
        ]}
      >
        <div className="mb-2">
          <label className="text-muted" style={{ fontSize: '0.85rem' }}>
            {t('Struttura business')}
          </label>
          <div style={{ fontWeight: 600 }}>{selectedStruttura?.name}</div>
        </div>
        <div>
          <label className="mb-1">{t('Account *')}</label>
          <Select
            showSearch
            allowClear
            className="w-100"
            placeholder={t('Seleziona account')}
            optionFilterProp="children"
            filterOption={filterOption}
            value={accountToAdd}
            onChange={(v) => setAccountToAdd(v as number | undefined)}
            getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
          >
            {accountsAvailableForAdd.map((a) => {
              const id = getAccountId(a);
              return (
                <Select.Option key={id} value={id}>
                  {a.nome}
                </Select.Option>
              );
            })}
          </Select>
        </div>
      </AntModal>
    </div>
  );
};

export default AccountBusinessStructureCrud;
